import { useState, useMemo } from "react"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { api } from "../lib/api"
import { useAuth } from "../lib/auth"

interface AuditEntry {
  id: string
  tenantId: string | null
  occurredAt: string
  actorUserId: string | null
  actorIp: string | null
  actorUserAgent: string | null
  action: string
  resourceType: string
  resourceId: string
  beforeState: unknown
  afterState: unknown
  prevHash: string | null
  thisHash: string
}

interface AuditListResponse {
  entries: AuditEntry[]
  nextCursor: string | null
}

interface VerifyResponse {
  valid: boolean
  checked: number
  brokenAtId: string | null
}

const ACTION_PILL: Record<string, string> = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  approve: "bg-amber-100 text-amber-700",
  compute: "bg-purple-100 text-purple-700",
  login: "bg-slate-100 text-slate-600",
}

function pillClass(action: string): string {
  // Match by suffix: "employee.create" → look up "create".
  for (const k of Object.keys(ACTION_PILL)) {
    if (action.endsWith(`.${k}`) || action === k || action.includes(`.${k}.`)) return ACTION_PILL[k]
  }
  return "bg-slate-100 text-slate-600"
}

function fmtTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString("en-GB", { dateStyle: "short", timeStyle: "medium" })
}

export function AuditLogPage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const { user, memberships } = useAuth()
  const [filter, setFilter] = useState("")
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Resolve tenantId from active slug. If memberships are empty (page-direct
  // reload before login refresh), the audit query stays disabled.
  const tenantId = useMemo(() => {
    return memberships.find((m) => m.tenantSlug === slug)?.tenantId ?? null
  }, [memberships, slug])

  const audit = useQuery({
    queryKey: ["audit", slug, tenantId],
    enabled: !!tenantId,
    queryFn: () =>
      api<AuditListResponse>(`/v1/audit?tenantId=${tenantId}&limit=200`),
  })

  // Verify endpoint is platform-admin only — show only when applicable.
  const verify = useQuery({
    queryKey: ["audit-verify"],
    enabled: !!user?.isPlatformAdmin,
    queryFn: () => api<VerifyResponse>("/v1/audit/verify"),
    refetchInterval: 60_000,
  })

  const filtered = useMemo(() => {
    if (!audit.data) return []
    const f = filter.toLowerCase().trim()
    if (!f) return audit.data.entries
    return audit.data.entries.filter(
      (e) =>
        e.action.toLowerCase().includes(f) ||
        e.resourceType.toLowerCase().includes(f) ||
        e.resourceId.toLowerCase().includes(f),
    )
  }, [audit.data, filter])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Audit log</h1>
          <p className="text-xs text-slate-500 mt-1">
            Tenant <span className="font-mono">{slug}</span> · hash-chained, append-only
          </p>
        </div>
        {user?.isPlatformAdmin && verify.data && (
          <div
            className={`text-xs px-3 py-1.5 rounded border ${
              verify.data.valid
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-red-50 text-red-800 border-red-200"
            }`}
          >
            {verify.data.valid
              ? `✓ Hash chain valid (${verify.data.checked.toLocaleString()} entries)`
              : `✗ Chain broken at ID ${verify.data.brokenAtId}`}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by action, resource type, or resource ID"
            className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-sm focus:border-brand-500 focus:outline-none"
          />
          <button
            onClick={() => audit.refetch()}
            disabled={audit.isFetching}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded"
          >
            {audit.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {audit.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {audit.error && <div className="p-6 text-sm text-red-600">{(audit.error as Error).message}</div>}
        {!tenantId && memberships.length === 0 && (
          <div className="p-6 text-sm text-slate-500">
            Membership not loaded yet. <a href="/people/login" className="text-brand-600">Sign in again</a> to refresh.
          </div>
        )}

        {audit.data && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400">
            {filter ? `No entries match "${filter}".` : "No audit entries yet."}
          </div>
        )}

        {filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Action</th>
                <th className="text-left px-4 py-2 font-medium">Resource</th>
                <th className="text-left px-4 py-2 font-medium">Resource ID</th>
                <th className="text-left px-4 py-2 font-medium">Actor IP</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <FragmentRow
                  key={e.id}
                  entry={e}
                  expanded={expandedId === e.id}
                  onToggle={() => setExpandedId(expandedId === e.id ? null : e.id)}
                />
              ))}
            </tbody>
          </table>
        )}

        {audit.data?.nextCursor && (
          <div className="px-4 py-2 border-t border-slate-200 text-xs text-slate-400">
            Showing first 200 entries. Older entries available via API cursor pagination — UI paging will land in a follow-up.
          </div>
        )}
      </div>
    </div>
  )
}

function FragmentRow(props: { entry: AuditEntry; expanded: boolean; onToggle: () => void }) {
  const e = props.entry
  return (
    <>
      <tr className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer" onClick={props.onToggle}>
        <td className="px-4 py-1.5 font-mono text-xs whitespace-nowrap">{fmtTime(e.occurredAt)}</td>
        <td className="px-4 py-1.5">
          <span className={`inline-block px-2 py-0.5 text-xs rounded font-mono ${pillClass(e.action)}`}>
            {e.action}
          </span>
        </td>
        <td className="px-4 py-1.5 font-mono text-xs">{e.resourceType}</td>
        <td className="px-4 py-1.5 font-mono text-xs text-slate-500 truncate max-w-[200px]">{e.resourceId}</td>
        <td className="px-4 py-1.5 font-mono text-xs text-slate-500">{e.actorIp ?? "—"}</td>
        <td className="px-4 py-1.5 text-right text-xs text-slate-400">{props.expanded ? "▾" : "▸"}</td>
      </tr>
      {props.expanded && (
        <tr className="border-b border-slate-100 bg-slate-50">
          <td colSpan={6} className="px-4 py-3">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <div className="text-slate-500 mb-1">Before</div>
                <pre className="bg-white border border-slate-200 rounded p-2 overflow-auto max-h-64 font-mono">
                  {e.beforeState ? JSON.stringify(e.beforeState, null, 2) : "—"}
                </pre>
              </div>
              <div>
                <div className="text-slate-500 mb-1">After</div>
                <pre className="bg-white border border-slate-200 rounded p-2 overflow-auto max-h-64 font-mono">
                  {e.afterState ? JSON.stringify(e.afterState, null, 2) : "—"}
                </pre>
              </div>
            </div>
            <div className="mt-3 text-[10px] text-slate-400 font-mono break-all">
              <div>Entry ID: {e.id}</div>
              <div>Hash: {e.thisHash}</div>
              {e.prevHash && <div>Prev: {e.prevHash}</div>}
              {e.actorUserAgent && <div>UA: {e.actorUserAgent}</div>}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
